import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import { Feed } from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../../../store";

import { useRequestHandlers } from "../../../app/hooks/useRequestHandlers";
import { handleFeed } from "../../../feed/handleFeed";
import { handleProduct } from "../../../product/handleProduct";
import { Button } from "../../../ui/layout/molecules/Button";
import { NamedTime } from "../../../ui/layout/molecules/NamedTime";
import { CollapsedFields } from "../../../components/GeneralUI/CollapsedFields/CollapsedFields";
import { getFieldsProps } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/helpers/getFieldsProps";
import {
  EMPTY_FEED,
  feedRenderProps,
  FEED_REQUIRED_UNIQUE_KEYS,
} from "./constants";
import { FieldsGroupWrapper } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/FieldsGroupWrapper";
import { useInputsValid } from "../../../ui/layout/detailedPages/ValidationContext";
import { PureVoidFunc } from "../../../helpers/functions/types";

const formatTime = (timeUnit: number) =>
  timeUnit < 10 ? `0${timeUnit}` : `${timeUnit}`;

// Rendered inside the slide-over's ValidationProvider: invalid feed inputs block it like Save changes,
// since importing saves the feeds first and the backend doesn't validate feed URLs.
const StartImportButton: FC<{
  allowTrigger: boolean;
  saveSettings: boolean;
  onImport: PureVoidFunc;
}> = ({ allowTrigger, saveSettings, onImport }) => {
  const isInputsValid = useInputsValid();
  return (
    <Button
      disabled={!allowTrigger || !isInputsValid}
      title={`${saveSettings ? "Save settings and " : ""}Start Import`}
      buttonType="primary"
      onClick={onImport}
      className="acceptButton w-full"
    />
  );
};

export const FeedSourcesBlock: FC = () => {
  const [feedUpdatingState, setFeedUpdatingState] = useState<boolean>(true);
  const { companyId } = useParams();
  const siteId = useAppSelector((state) => state.site.id);
  const allowedServices = useAppSelector(
    (state) => state.company?.company?.allowedServices,
  );

  const isFeedProcessingDisabled = useMemo(
    () => !allowedServices?.length,
    [allowedServices?.length],
  );

  const handlerParams = useRequestHandlers();
  const { getProducts } = handleProduct(handlerParams, siteId);
  const { handleScheduleFeed, handleTriggerFeed, handleRefreshFeed } =
    handleFeed(handlerParams, companyId, siteId);

  const { feedIsScheduling, feedIsTriggering } = useAppSelector(
    (state) => state.feed,
  );

  const { loading } = useAppSelector((state) => state.products.products);

  const { feedUrl, isActive, triggerTimestamp, feeds, scheduledAt } =
    useAppSelector((state) => state.products.products.scheduledImport);

  const initFeeds: Feed[] = useMemo(
    () =>
      feeds?.length
        ? feeds
        : feedUrl
          ? [{ url: feedUrl, entityType: "product" }]
          : [EMPTY_FEED],
    [feeds, feedUrl],
  );

  const initTimestamp = useMemo(() => {
    return scheduledAt
      ? `${formatTime(scheduledAt.hour)}:${formatTime(scheduledAt.minute)}`
      : triggerTimestamp;
  }, [scheduledAt, triggerTimestamp]);

  const [siteFeeds, setSiteFeeds] = useState<Feed[]>(initFeeds);

  const [isScheduling, setIsScheduling] = useState<boolean>(isActive);
  const [scheduleTime, setScheduleTime] = useState<string>(initTimestamp);

  const resetModalValues = useCallback(() => {
    setFeedUpdatingState(loading);
    setSiteFeeds(initFeeds);
    setIsScheduling(isActive);
    setScheduleTime(initTimestamp);
  }, [isActive, initTimestamp, initFeeds, loading]);

  useEffect(() => {
    if (feedUpdatingState) {
      setFeedUpdatingState(loading || feedIsScheduling || feedIsTriggering);
    }
  }, [loading, feedIsScheduling, feedIsTriggering, feedUpdatingState]);

  const scheduleFeedHandler = async () =>
    await handleScheduleFeed(
      isScheduling,
      scheduleTime,
      siteFeeds.filter((feed) => !!feed.url.length),
    );

  const triggerFeedHandler = async () => {
    const triggeringResult = await handleTriggerFeed();
    if (triggeringResult) {
      handleRefreshFeed();
      return true;
    }
  };

  const {
    handleRemove,
    handleAdd,
    areSameFields,
    filterOutEmptyFields,
    fieldsRenderData,
  } = useMemo(
    () =>
      getFieldsProps<Feed>(
        setSiteFeeds,
        EMPTY_FEED,
        FEED_REQUIRED_UNIQUE_KEYS,
        isFeedProcessingDisabled,
      ),
    [isFeedProcessingDisabled],
  );

  const allowTriggerFeeds = useMemo(
    () => !isFeedProcessingDisabled && !!filterOutEmptyFields(siteFeeds).length,
    [siteFeeds, isFeedProcessingDisabled, filterOutEmptyFields],
  );

  const feedsChanged = useMemo(
    () => !areSameFields(initFeeds, siteFeeds),
    [initFeeds, siteFeeds, areSameFields],
  );

  const saveSettings = useMemo(
    () =>
      allowTriggerFeeds &&
      (feedsChanged ||
        !(isScheduling === isActive) ||
        !(scheduleTime === initTimestamp)),
    [
      allowTriggerFeeds,
      feedsChanged,
      isScheduling,
      scheduleTime,
      isActive,
      initTimestamp,
    ],
  );

  const importFeed = async () => {
    if (saveSettings) await scheduleFeedHandler();
    const isSuccess = await triggerFeedHandler();
    getProducts();
    return isSuccess;
  };

  const renderFeeds = useMemo(
    () => fieldsRenderData(siteFeeds, feedRenderProps),
    [siteFeeds, fieldsRenderData],
  );

  return (
    <FieldsGroupWrapper
      fieldsType="feed"
      initFieldsCount={initFeeds.length}
      initConfig={resetModalValues}
      addFieldHandler={handleAdd}
      removeFieldHandler={handleRemove}
      submitFieldHandler={scheduleFeedHandler}
      fieldRender={renderFeeds}
      allowSubmit={saveSettings}
      disabledMessage={
        isFeedProcessingDisabled
          ? "Please activate a valid company plan to process the feeds."
          : undefined
      }
      isLoaded={!feedUpdatingState}
      importantFields={FEED_REQUIRED_UNIQUE_KEYS}
    >
      <div className="flex flex-row flex-wrap justify-between gap-2 my-4">
        <StartImportButton
          allowTrigger={allowTriggerFeeds}
          saveSettings={saveSettings}
          onImport={importFeed}
        />
      </div>

      <CollapsedFields
        title="Schedule daily import"
        active={isScheduling}
        resetFields={() => setIsScheduling(false)}
        activateFields={() => setIsScheduling(true)}
        disabled={isFeedProcessingDisabled}
      >
        <NamedTime
          value={scheduleTime}
          onChange={setScheduleTime}
          title="Import time"
          disabled={isFeedProcessingDisabled}
        />
      </CollapsedFields>
    </FieldsGroupWrapper>
  );
};
